import type {
  SharedResourceShareCapability,
  SharedResourceShareFormUser,
} from "./shared-resource-share-form-adapter";
import type { SsoOrganizationPickerUser } from "./sso-organization-user-picker-types";

export const SHARED_RESOURCE_DIRECT_SHARE_CAPABILITIES = [
  "view",
  "reference",
  "clone",
] as const satisfies readonly SharedResourceShareCapability[];

export type SharedResourceShareUserIdentity = {
  id: number;
  username?: string | null;
  full_name?: string | null;
  display_name?: string | null;
  phone?: string | null;
  organization_name?: string | null;
  organization_unit_names?: string[] | null;
  organization_memberships_summary?: Array<{
    organization_name?: string | null;
    organization_unit_name?: string | null;
  }>;
};

export function toSharedResourcePickerUser<TCapability extends SharedResourceShareCapability>(
  user: SharedResourceShareFormUser<TCapability>,
): SsoOrganizationPickerUser {
  return {
    id: user.user_id,
    username: user.username,
    display_name: user.full_name || user.username,
    organization_id: 0,
    organization_name: user.organization_name || "",
    organization_unit_ids: [],
    organization_unit_names: user.organization_unit_names,
    membership_ids: [],
    full_name: user.full_name,
    phone: user.phone || null,
    status: "active",
    is_online: false,
    is_superuser: false,
    expires_at: null,
    platform_roles: [],
    application_roles_summary: [],
    organization_memberships_summary: [],
    platform_role_assignments: [],
    custom_fields_json: {},
    created_at: "",
    updated_at: "",
  };
}

export function fromSharedResourcePickerUser<TCapability extends SharedResourceShareCapability>(
  user: SsoOrganizationPickerUser,
  previous: SharedResourceShareFormUser<TCapability> | undefined,
  defaultCapabilities: readonly TCapability[],
): SharedResourceShareFormUser<TCapability> {
  return {
    user_id: user.id,
    username: user.username,
    full_name: user.display_name,
    phone: user.phone || null,
    organization_name: user.organization_name || null,
    organization_unit_names: user.organization_unit_names,
    capabilities: previous?.capabilities?.length
      ? [...previous.capabilities]
      : [...defaultCapabilities],
  };
}

export function mergeIdentityUserIntoSharedResourceShareUser<
  TCapability extends SharedResourceShareCapability,
>(
  user: SharedResourceShareFormUser<TCapability>,
  detail?: SharedResourceShareUserIdentity,
): SharedResourceShareFormUser<TCapability> {
  if (!detail) {
    return user;
  }
  const membership = detail.organization_memberships_summary?.[0];
  const unitNames =
    detail.organization_unit_names ??
    detail.organization_memberships_summary
      ?.map((item) => item.organization_unit_name)
      .filter((value): value is string => String(value || "").trim().length > 0) ??
    [];
  const username = detail.username || user.username;
  const fullName = detail.full_name || detail.display_name || user.full_name || username;
  const phone = detail.phone ?? user.phone;
  return {
    ...user,
    username,
    full_name: fullName,
    ...(phone === undefined ? {} : { phone }),
    organization_name:
      detail.organization_name || membership?.organization_name || user.organization_name || null,
    organization_unit_names: unitNames,
  };
}
